import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageFlatList } from 'components/Page';
import React from 'react';
import { useDispatch } from 'react-redux';
import {
  eventAssessmentTemplateQuestionScreenDone,
  NAVIGATE_MODAL_BACK,
} from 'store/actions';
import { selectAseSetupLibraryItemList } from 'store/remote/selectors';
import { useSelector } from 'hooks/useSelector';
import { ICONS } from 'components/Icon';
import { ListRenderItem } from '@shopify/flash-list';
import { difference, intersection, union, without } from 'lodash';
import { useEsc } from 'hooks/useEsc';
import { arrayHasSameMembers } from 'lib/arrayHasSameMembers/arrayHasSameMembers';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { theme } from 'lib/theme';
import { QuestionListItem } from './ListItems/QuestionListItem';
import { SectionListItem } from './ListItems/SectionListItem';
import { FolderListItem } from './ListItems/FolderListItem';

export const QuestionListScreen: React.FC<
  ModalScreenProps<ModalScreens.ASESETUPTEMPLATEQUESTIONLIST>
> = ({ route, navigation }) => {
  const dispatch = useDispatch();
  const confirmUnsaved = useConfirmUnsaved();
  const { questionIds, eventKey: incomingValueKey } = route.params;

  const [selectedQuestionIds, setSelectedQuestionIds] =
    React.useState(questionIds);

  const items = useSelector(selectAseSetupLibraryItemList);

  const checkIsDirty = () =>
    !arrayHasSameMembers(questionIds, selectedQuestionIds);

  const { sectionHasQuestions } = React.useMemo((): {
    sectionHasQuestions: Record<string, number[]>;
  } => {
    const sq: Record<string, number[]> = {};
    let sid: number = 0;
    items.forEach((item) => {
      if (item.type === 'section') {
        sid = item.id;
      } else {
        if (!sq[sid]) {
          sq[sid] = [];
        }
        sq[sid]!.push(item.id);
      }
    });
    return {
      sectionHasQuestions: sq,
    };
  }, [items]);

  const handleClose = () => {
    if (checkIsDirty()) {
      confirmUnsaved({
        onSave: handlePressSave,
        onDiscard: () => dispatch(NAVIGATE_MODAL_BACK()),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  };

  const handlePressSave = React.useCallback(() => {
    dispatch(NAVIGATE_MODAL_BACK());
    dispatch(
      eventAssessmentTemplateQuestionScreenDone({
        key: incomingValueKey,
        value: selectedQuestionIds,
      }),
    );
  }, [dispatch, incomingValueKey, selectedQuestionIds]);

  const handlePressQuestion = React.useCallback(
    (questionId: number) =>
      setSelectedQuestionIds((cur) =>
        cur.includes(questionId)
          ? without(cur, questionId)
          : [...cur, questionId],
      ),
    [],
  );

  const handleSelectSection = React.useCallback(
    (sectionId: number) =>
      setSelectedQuestionIds((cur) =>
        union(cur, sectionHasQuestions[sectionId]!),
      ),
    [sectionHasQuestions],
  );
  const handleDeselectSection = React.useCallback(
    (sectionId: number) =>
      setSelectedQuestionIds((cur) =>
        difference(cur, sectionHasQuestions[sectionId]!),
      ),
    [sectionHasQuestions],
  );

  const renderItem: ListRenderItem<
    ReturnType<typeof selectAseSetupLibraryItemList>[number]
  > = React.useCallback(
    ({ item, extraData }) => {
      if (item.type === 'question') {
        return (
          <QuestionListItem
            id={item.id}
            selected={extraData.selectedQuestionIds.includes(item.id)}
            onPress={handlePressQuestion}
            isLastQuestionOfSection={!!item.last}
            isLastQuestionOfFolder={!!item.lastQuestion}
          />
        );
      }
      if (item.type === 'section') {
        return (
          <SectionListItem
            id={item.id}
            onSelectAll={handleSelectSection}
            onDeselectAll={handleDeselectSection}
            totalCount={sectionHasQuestions[item.id]!.length}
            selectedCount={
              intersection(
                extraData.selectedQuestionIds,
                sectionHasQuestions[item.id],
              ).length
            }
            isFirstSection={!!item.firstSection}
          />
        );
      }
      return <FolderListItem id={item.id} />;
    },
    [
      handleDeselectSection,
      handlePressQuestion,
      handleSelectSection,
      sectionHasQuestions,
    ],
  );

  const isDirtyVar = checkIsDirty();
  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirtyVar });
  }, [navigation, isDirtyVar]);
  useEsc(handleClose);

  return (
    <PageFlatList
      header={
        <NavigationHeader
          heading="Checklists & Questions"
          subheading={`${selectedQuestionIds.length} Selected`}
          onPressCancelButton={handleClose}
          onPressSaveButton={handlePressSave}
          saveButtonText="Done"
        />
      }
      emptyConfig={{
        icon: ICONS.RBPlectrumLight,
        text: 'No Questions or Checklists',
      }}
      contentPaddingTop={theme.paddingVertical - theme.paddingHorizontalHalf}
      listConfig={{
        data: items,
        extraData: { selectedQuestionIds },
        keyExtractor: (item) => `${item.type}-${item.id}`,
        getItemType: (item) => item.type,
        renderItem,
        ItemSeparatorComponent: undefined,
        stickyHeaderIndices: items.reduce<number[]>((acc, item, index) => {
          if (item.type === 'folder') {
            acc.push(index);
          }
          return acc;
        }, []),
      }}
    />
  );
};
